/**
 * Global toast layer.
 *
 * Every RulesError the battlefield catches lands here as its `reason` string,
 * which is the sentence the rules doc says the player should see. Toasts stack
 * bottom-right on desktop and top-center on mobile, auto-dismiss, and can be
 * dismissed early. Copy voice: short, dry, never blaming the player.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { UiIcon } from '@/ui/icons/UiIcon';
import styles from './ToastProvider.module.css';

export type ToastTone = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** Optional second line, e.g. the rules code behind a rejection. */
  detail?: string;
}

export interface ToastApi {
  push(message: string, tone?: ToastTone, detail?: string): void;
  dismiss(id: number): void;
  clear(): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/*
 * Toasts are for the engine rejecting an action the player actually confirmed.
 * They are not a place to explain why a card is dim: that belongs on the card,
 * where the player is already looking. Kept short and shallow so the board is
 * never buried, which matters most on a phone where they sit over the top rows.
 */
const LIFETIME_MS = 2500;
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone = 'info', detail?: string) => {
      const id = nextId.current++;
      setToasts((list) => [...list, { id, message, tone, detail }].slice(-MAX_VISIBLE));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), LIFETIME_MS),
      );
    },
    [dismiss],
  );

  const clear = useCallback(() => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
    setToasts([]);
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss, clear }), [push, dismiss, clear]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.layer} role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={styles.toast} data-tone={toast.tone}>
            <span className={styles.bar} aria-hidden="true" />
            <span className={styles.body}>
              <span className={styles.message}>{toast.message}</span>
              {toast.detail ? <span className={styles.detail}>{toast.detail}</span> : null}
            </span>
            <button className={styles.close} onClick={() => dismiss(toast.id)} aria-label="Dismiss">
              <UiIcon name="close" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Toasts outside a provider are a no-op, so tests can render bare components. */
const NOOP: ToastApi = { push: () => {}, dismiss: () => {}, clear: () => {} };

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP;
}
